/**
 * Where an edit is made, for the change ledger (docs/activity-ledger/01-plan.md).
 *
 * Every mutating request carries `X-Scandi-Surface` (the clip editor, the
 * script editor, a line page...) and `X-Scandi-Session` (this tab). The
 * surface is the one the person last pointed at or typed in - the nearest
 * `[data-surface]` around what they touched - so an edit in the script editor
 * inside the presentation page is the script editor's, and a debounced
 * autosave that fires a moment later still is. A dialog rendered outside its
 * page (a portal) falls back to the innermost surface mounted.
 */
import type { ClientSurface } from "@scandi/shared";

/** One id per tab load: random, nothing else. */
const SESSION_ID = (() => {
  try {
    return `t${crypto.randomUUID().replace(/-/g, "").slice(0, 20)}`;
  } catch {
    return `t${Math.random().toString(36).slice(2, 14)}${Date.now().toString(36)}`;
  }
})();

const mounted: ClientSurface[] = [];
let touched: { surface: ClientSurface; at: number } | null = null;
/** The surface last typed in: an edit's own autosave or flush often fires as the pointer goes elsewhere ("Done"). */
let typed: { surface: ClientSurface; at: number } | null = null;
/** A surface just closed, whose last write (a flush on leave) is still on its way. */
let leaving: { surface: ClientSurface; at: number } | null = null;
let listening = false;

/** How long a touch speaks for the requests after it (a slow autosave included). */
const TOUCH_MS = 5 * 60_000;
/** How long typing, or a surface closing, outranks a later click elsewhere. */
const TYPED_MS = 4_000;

function hostOf(event: Event): ClientSurface | undefined {
  const target = event.target as Element | null;
  const host = target && typeof target.closest === "function" ? target.closest<HTMLElement>("[data-surface]") : null;
  return host?.dataset.surface as ClientSurface | undefined;
}

function onTouch(event: Event): void {
  const surface = hostOf(event);
  if (surface) touched = { surface, at: Date.now() };
}

function onType(event: Event): void {
  const surface = hostOf(event);
  if (surface) typed = touched = { surface, at: Date.now() };
}

function listen(): void {
  if (listening || typeof document === "undefined") return;
  listening = true;
  // Capture: a handler that stops propagation still leaves the touch recorded.
  document.addEventListener("pointerdown", onTouch, { capture: true, passive: true });
  document.addEventListener("keydown", onType, { capture: true, passive: true });
  document.addEventListener("input", onType, { capture: true, passive: true });
}

/** A surface's root mounted; answers the unmount. */
export function enterSurface(surface: ClientSurface): () => void {
  listen();
  mounted.push(surface);
  return () => {
    const at = mounted.lastIndexOf(surface);
    if (at >= 0) mounted.splice(at, 1);
    if (!mounted.includes(surface) && (typed?.surface === surface || touched?.surface === surface)) leaving = { surface, at: Date.now() };
  };
}

/** The surface a write made now comes from, if any. */
export function currentSurface(): ClientSurface | null {
  const now = Date.now();
  if (leaving && now - leaving.at < TYPED_MS) return leaving.surface;
  if (typed && now - typed.at < TYPED_MS && mounted.includes(typed.surface)) return typed.surface;
  if (touched && now - touched.at < TOUCH_MS && mounted.includes(touched.surface)) return touched.surface;
  return mounted.at(-1) ?? null;
}

/** This tab's id: the presence heartbeat's too (`lib/presence.ts`). */
export const tabId = (): string => SESSION_ID;

/** The two headers for a mutating request. */
export function surfaceHeaders(): Record<string, string> {
  const surface = currentSurface();
  return surface ? { "x-scandi-surface": surface, "x-scandi-session": SESSION_ID } : { "x-scandi-session": SESSION_ID };
}
